import {
    Action,
    IAgentRuntime,
    Memory,
    State,
    HandlerCallback,
    elizaLogger,
} from "@elizaos/core";
import { initCotiWalletProvider } from "../providers/wallet";
import { ethers } from "ethers";
import { z } from "zod";

// Schema for getting token URI parameters
export const GetPrivateERC721TokenURISchema = z.object({
    tokenAddress: z.string().describe("ERC721 token contract address"),
    tokenId: z.string().describe("ID of the NFT token"),
});

export type GetPrivateERC721TokenURIParams = z.infer<typeof GetPrivateERC721TokenURISchema>;

// Basic ERC721 ABI for token URI
const ERC721_ABI = [
    "function tokenURI(uint256 tokenId) view returns (string)",
    "function ownerOf(uint256 tokenId) view returns (address)",
    "function name() view returns (string)",
    "function symbol() view returns (string)"
];

export const getPrivateErc721TokenUriAction: Action = {
    name: "GET_PRIVATE_ERC721_TOKEN_URI",
    similes: [
        "GET_TOKEN_URI",
        "GET_NFT_URI",
        "GET_METADATA_URI",
        "GET_NFT_METADATA",
        "SHOW_TOKEN_URI",
        "TOKEN_METADATA"
    ],
    description: "Get the metadata URI for a private ERC721 NFT token on the COTI blockchain.",
    validate: async (runtime: IAgentRuntime, message: Memory): Promise<boolean> => {
        try {
            const text = message.content?.text || "";
            elizaLogger.info(`GET_PRIVATE_ERC721_TOKEN_URI validation - text: "${text}"`);
            
            // Check for URI/metadata keywords
            const isUriQuery = /\b(uri|metadata|get.*metadata|show.*uri)\b/i.test(text);
            elizaLogger.info(`GET_PRIVATE_ERC721_TOKEN_URI validation - isUriQuery: ${isUriQuery}`);
            
            // Check if message content has required fields
            if (message.content?.tokenAddress && message.content?.tokenId) {
                elizaLogger.info(`GET_PRIVATE_ERC721_TOKEN_URI validation - structured params found`);
                return true;
            }

            // Check if text contains address and token ID
            const addressMatch = text.match(/0x[a-fA-F0-9]{40}/);
            const tokenIdMatch = text.match(/token\s*id\s*(\d+)|id\s*(\d+)/i);
            
            elizaLogger.info(`GET_PRIVATE_ERC721_TOKEN_URI validation - address found: ${!!addressMatch}`);
            elizaLogger.info(`GET_PRIVATE_ERC721_TOKEN_URI validation - tokenId found: ${!!(tokenIdMatch?.[1] || tokenIdMatch?.[2])}`);

            if (isUriQuery && addressMatch && tokenIdMatch) {
                elizaLogger.info(`GET_PRIVATE_ERC721_TOKEN_URI validation - returning true`);
                return true;
            }

            elizaLogger.info(`GET_PRIVATE_ERC721_TOKEN_URI validation - returning false`);
            return false;
        } catch (error) {
            elizaLogger.error(`GET_PRIVATE_ERC721_TOKEN_URI validation error:`, error);
            return false;
        }
    },
    handler: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State | undefined,
        options: any = {},
        callback?: HandlerCallback
    ) => {
        try {
            elizaLogger.info("Starting GET_PRIVATE_ERC721_TOKEN_URI action");

            let params: GetPrivateERC721TokenURIParams;

            // Try to get parameters from message content
            if (message.content?.tokenAddress && message.content?.tokenId) {
                params = GetPrivateERC721TokenURISchema.parse(message.content);
            } else {
                // Extract from message text
                const text = message.content?.text || "";
                const addressMatch = text.match(/0x[a-fA-F0-9]{40}/);
                const tokenIdMatch = text.match(/token\s*id\s*(\d+)|id\s*(\d+)/i);
                
                if (!addressMatch || !tokenIdMatch) {
                    throw new Error("Could not extract required parameters from message");
                }
                
                params = {
                    tokenAddress: addressMatch[0],
                    tokenId: tokenIdMatch[1] || tokenIdMatch[2]
                };
            }

            const walletProvider = initCotiWalletProvider(runtime);
            const provider = walletProvider.getProvider();

            // Create contract instance
            const contract = new ethers.Contract(
                params.tokenAddress,
                ERC721_ABI,
                provider
            );

            elizaLogger.info(`Getting token URI for token ${params.tokenId} from contract ${params.tokenAddress}`);

            // Get token URI
            const tokenUri = await contract.tokenURI(params.tokenId);
            
            // Get additional token info
            const owner = await contract.ownerOf(params.tokenId);
            let name = "Unknown";
            let symbol = "Unknown";
            
            try {
                name = await contract.name();
                symbol = await contract.symbol();
            } catch (e) {
                elizaLogger.warn("Could not get contract name/symbol:", e);
            }

            const responseText = `Private ERC721 Token Information:
Contract: ${name} (${symbol})
Contract Address: ${params.tokenAddress}
Token ID: ${params.tokenId}
Owner: ${owner}
Metadata URI: ${tokenUri}`;

            if (callback) {
                await callback({
                    text: responseText,
                    content: {
                        success: true,
                        tokenAddress: params.tokenAddress,
                        tokenId: params.tokenId,
                        tokenUri,
                        owner,
                        contractName: name,
                        contractSymbol: symbol,
                    },
                });
            }

            return {
                text: responseText,
                success: true,
                data: {
                    tokenAddress: params.tokenAddress,
                    tokenId: params.tokenId,
                    tokenUri,
                    owner,
                    contractName: name,
                    contractSymbol: symbol,
                },
            };
        } catch (error) {
            elizaLogger.error("Error in GET_PRIVATE_ERC721_TOKEN_URI action:", error);
            const errorMessage = `Failed to get private ERC721 token URI: ${error instanceof Error ? error.message : String(error)}`;

            if (callback) {
                await callback({
                    text: errorMessage,
                    content: { success: false, error: errorMessage },
                });
            }

            return {
                text: errorMessage,
                success: false,
                error: error instanceof Error ? error : new Error(String(error)),
            };
        }
    },
    examples: [
        [
            {
                name: "{{user1}}",
                content: {
                    text: "Get the metadata URI for NFT token 456",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "I'll retrieve and decrypt the private metadata URI for that NFT.",
                    action: "GET_PRIVATE_ERC721_TOKEN_URI",
                },
            },
        ],
    ],
};
